import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, GraduationCap, ArrowUpRight, LogOut, CheckSquare, Square, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import UpdateUser from '../api/UpdateUser';

const ManageClassModal = ({
    isOpen,
    onClose,
    currentClass,
    students, // array of student members in currentClass
    availableClasses, // array of existing unique class names for convenience
    currentUser,
    onRefresh
}) => {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [targetClass, setTargetClass] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    useEffect(() => {
        if (isOpen && students) {
            // By default, select all students in this class
            const allIds = new Set(students.map(s => s.ID || s.id));
            setSelectedIds(allIds);
            setTargetClass('');
            setStatusMessage(null);
            setIsSubmitting(false);
        }
    }, [isOpen, students]);

    if (!isOpen) return null;

    const isAllSelected = students.length > 0 && selectedIds.size === students.length;

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(students.map(s => s.ID || s.id)));
        }
    };

    const toggleSelectUser = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const handlePromoteClass = async () => {
        if (!targetClass.trim()) {
            alert("Veuillez indiquer ou choisir la nouvelle classe (ex: 4ème B, 2nde A...).");
            return;
        }
        if (selectedIds.size === 0) {
            alert("Veuillez sélectionner au moins un élève.");
            return;
        }

        setIsSubmitting(true);
        setStatusMessage(`Mise à jour vers la classe "${targetClass.trim()}"...`);

        const selectedStudents = students.filter(s => selectedIds.has(s.ID || s.id));
        let successCount = 0;

        for (const student of selectedStudents) {
            try {
                const memberId = student.ID || student.id;
                await UpdateUser(memberId, {
                    name: student.name,
                    email: student.email,
                    tel: student.tel || "",
                    role: student.role || "Étudiant",
                    classe: targetClass.trim(),
                    code_etablissement: currentUser.code_etablissement
                });
                successCount++;
            } catch (err) {
                console.error("Erreur lors de la mise à jour de l'élève:", student.name, err);
            }
        }

        if (onRefresh) await onRefresh();
        setIsSubmitting(false);
        onClose();
    };

    const handleTransferToExitClass = async () => {
        if (selectedIds.size === 0) {
            alert("Veuillez sélectionner au moins un élève.");
            return;
        }

        const confirmMsg = `Voulez-vous vraiment transférer ${selectedIds.size} élève(s) dans la classe de sortie ("Sortie d'établissement") ?`;
        if (!window.confirm(confirmMsg)) return;

        setIsSubmitting(true);
        setStatusMessage(`Transfert vers la classe de sortie...`);

        const selectedStudents = students.filter(s => selectedIds.has(s.ID || s.id));
        const exitClassName = "Sortie";

        for (const student of selectedStudents) {
            try {
                const memberId = student.ID || student.id;
                await UpdateUser(memberId, {
                    name: student.name,
                    email: student.email,
                    tel: student.tel || "",
                    role: student.role || "Étudiant",
                    classe: exitClassName,
                    code_etablissement: currentUser.code_etablissement
                });
            } catch (err) {
                console.error("Erreur lors du transfert de l'élève:", student.name, err);
            }
        }

        if (onRefresh) await onRefresh();
        setIsSubmitting(false);
        onClose();
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-card backdrop-blur-2xl border border-border rounded-3xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
                                <Settings size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-foreground">Gestion de la classe : {currentClass}</h3>
                                <p className="text-xs text-muted-foreground">
                                    {students.length} élève(s) dans cette classe — {selectedIds.size} sélectionné(s)
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="text-muted-foreground hover:text-foreground transition-colors p-2 hover:bg-muted rounded-full"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6 overflow-y-auto flex-1">
                        {/* Target class input */}
                        <div className="bg-muted/40 border border-border p-4 rounded-2xl space-y-3">
                            <label className="block text-sm font-semibold text-foreground">
                                Sélectionner ou saisir la nouvelle classe supérieure :
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={targetClass}
                                    onChange={(e) => setTargetClass(e.target.value)}
                                    placeholder="Ex: 3ème B, 2nde A..."
                                    className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                />
                                {availableClasses && availableClasses.length > 0 && (
                                    <select
                                        onChange={(e) => setTargetClass(e.target.value)}
                                        className="bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                                        value=""
                                    >
                                        <option value="" disabled>Classes existantes...</option>
                                        {availableClasses.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Select All Row */}
                        <div className="flex items-center justify-between py-2 border-b border-border">
                            <button
                                onClick={toggleSelectAll}
                                className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                            >
                                {isAllSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                                <span>{isAllSelected ? 'Tout désélectionner' : 'Tout sélectionner'}</span>
                            </button>
                            <span className="text-xs text-muted-foreground font-medium">
                                {selectedIds.size} / {students.length} élèves retenus
                            </span>
                        </div>

                        {/* Student list */}
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                            {students.map((student) => {
                                const studentId = student.ID || student.id;
                                const isChecked = selectedIds.has(studentId);
                                return (
                                    <div
                                        key={studentId}
                                        onClick={() => toggleSelectUser(studentId)}
                                        className={`flex items-center justify-between p-3 rounded-2xl border transition-all cursor-pointer ${
                                            isChecked
                                                ? 'bg-primary/10 border-primary/40'
                                                : 'bg-muted/30 border-border hover:bg-muted/60'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                className={`p-1 rounded-lg text-primary ${
                                                    isChecked ? 'text-primary' : 'text-muted-foreground'
                                                }`}
                                            >
                                                {isChecked ? <CheckSquare size={20} /> : <Square size={20} />}
                                            </button>
                                            <img
                                                src={student.avatar || '/avatars/student.png'}
                                                alt={student.name}
                                                className="w-10 h-10 rounded-full border border-border object-cover"
                                            />
                                            <div>
                                                <h4 className="text-sm font-bold text-foreground">{student.name}</h4>
                                                <p className="text-xs text-muted-foreground">{student.email}</p>
                                            </div>
                                        </div>
                                        <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                                            {student.classe || currentClass}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="p-5 bg-muted/30 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
                        <button
                            onClick={handleTransferToExitClass}
                            disabled={isSubmitting || selectedIds.size === 0}
                            className="w-full sm:w-auto px-5 py-2.5 bg-amber-500/10 text-amber-500 border border-amber-500/20 font-bold rounded-xl hover:bg-amber-500/20 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                        >
                            <LogOut size={18} />
                            <span>Transférer dans la classe de sortie</span>
                        </button>

                        <button
                            onClick={handlePromoteClass}
                            disabled={isSubmitting || selectedIds.size === 0}
                            className="w-full sm:w-auto px-5 py-2.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 text-sm shadow-lg shadow-primary/20 disabled:opacity-50"
                        >
                            {isSubmitting ? (
                                <Loader2 size={18} className="animate-spin" />
                            ) : (
                                <ArrowUpRight size={18} />
                            )}
                            <span>Passer en classe supérieure</span>
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default ManageClassModal;
